// Yeni API fonksiyonları - SEAM entegrasyonu ve gelişmiş özellikler
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

// ========== Cloud Functions Callables ==========

const getRoomsCallable = httpsCallable(functions, "getRooms");
const getPricingCallable = httpsCallable(functions, "getPricing");
const getSystemSettingsCallable = httpsCallable(functions, "getSystemSettings");
const getAvailableSlotsCallable = httpsCallable(functions, "getAvailableSlots");
const createBookingCallable = httpsCallable(functions, "createBooking");
const cancelBookingCallable = httpsCallable(functions, "cancelBooking");
const listSeamDevicesCallable = httpsCallable(functions, "listSeamDevices");

// Dekont yönetimi
const uploadReceiptCallable = httpsCallable(functions, "uploadReceipt");
const approveReceiptCallable = httpsCallable(functions, "approveReceipt");
const rejectReceiptCallable = httpsCallable(functions, "rejectReceipt");
const addPenaltyCallable = httpsCallable(functions, "addPenalty");
const getPendingReceiptsCallable = httpsCallable(functions, "getPendingReceipts");

// Tekrarlayan dersler
const createRecurringEventCallable = httpsCallable(functions, "createRecurringEvent");
const updateRecurringEventCallable = httpsCallable(functions, "updateRecurringEvent");
const deleteRecurringEventCallable = httpsCallable(functions, "deleteRecurringEvent");
const getRecurringEventsCallable = httpsCallable(functions, "getRecurringEvents");

// ========== Exported Functions ==========

/**
 * Tüm odaları getir (seed eder)
 */
export const getRooms = async () => {
  try {
    const result = await getRoomsCallable();
    console.log("getRooms result:", result);
    if (!result || !result.data) {
      throw new Error("Geçersiz yanıt: data bulunamadı");
    }
    return result.data.rooms || [];
  } catch (error) {
    console.error("Error fetching rooms:", error);
    console.error("Error code:", error.code);
    console.error("Error message:", error.message);
    console.error("Error details:", error.details);
    throw new Error(error.message || "Odalar yüklenirken hata oluştu");
  }
};

/**
 * Fiyatlandırma bilgilerini getir
 */
export const getPricing = async () => {
  try {
    const result = await getPricingCallable();
    return result.data.pricing;
  } catch (error) {
    console.error("Error fetching pricing:", error);
    throw error;
  }
};

/**
 * Sistem ayarlarını getir (IBAN, çalışma saatleri, vb.)
 */
export const getSystemSettings = async () => {
  try {
    const result = await getSystemSettingsCallable();
    return result.data.settings;
  } catch (error) {
    console.error("Error fetching system settings:", error);
    throw error;
  }
};

/**
 * Belirli bir oda ve tarih için uygun saatleri getir
 * @param {string} roomId - Oda ID
 * @param {string} date - Tarih (YYYY-MM-DD formatında)
 */
export const getAvailableSlots = async (roomId, date) => {
  try {
    const result = await getAvailableSlotsCallable({ roomId, date });
    return result.data.slots;
  } catch (error) {
    console.error("Error fetching available slots:", error);
    throw error;
  }
};

/**
 * Yeni rezervasyon oluştur (SEAM PIN ile)
 * @param {Object} data - Rezervasyon bilgileri
 * @param {string} data.roomId - Oda ID
 * @param {string} data.date - Tarih (YYYY-MM-DD)
 * @param {string} data.startTime - Başlangıç saati (HH:mm)
 * @param {string} data.endTime - Bitiş saati (HH:mm)
 * @param {string} data.reservationType - "individual" | "group"
 * @param {number} data.numberOfPeople - Kişi sayısı (1-8)
 * @param {boolean} data.capacityAgreementAccepted
 * @param {boolean} data.receiptAgreementAccepted
 * @param {boolean} data.cancelPolicyAccepted
 */
export const createBooking = async (data) => {
  try {
    const result = await createBookingCallable(data);
    return result.data;
  } catch (error) {
    console.error("Error creating booking:", error);
    throw error;
  }
};

/**
 * Rezervasyonu iptal et (SEAM PIN siler)
 * @param {string} reservationId - Rezervasyon ID
 */
export const cancelBooking = async (reservationId) => {
  try {
    const result = await cancelBookingCallable({ reservationId });
    return result.data;
  } catch (error) {
    console.error("Error cancelling booking:", error);
    throw error;
  }
};

/**
 * SEAM cihazlarını listele (admin)
 */
export const listSeamDevices = async () => {
  try {
    const result = await listSeamDevicesCallable();
    return result.data.devices;
  } catch (error) {
    console.error("Error listing SEAM devices:", error);
    throw error;
  }
};

// ========== Helper Functions ==========

/**
 * Hafta başını (Pazartesi) hesapla
 */
export const getMonday = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Hafta sonunu (Pazar) hesapla
 */
export const getSunday = (date = new Date()) => {
  const monday = getMonday(date);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return sunday;
};

/**
 * Tarih mevcut hafta içinde mi?
 */
export const isWithinCurrentWeek = (date) => {
  const monday = getMonday();
  const sunday = getSunday();
  return date >= monday && date <= sunday;
};

const IYTE_EMAIL_PATTERN = /@(std\.)?iyte\.edu\.tr$/i;

export const isIyteDiscountEligible = (email = "") => IYTE_EMAIL_PATTERN.test(email.trim());

export const getPriceBreakdown = (reservationType, duration, userEmail = "", pricingRules = {}) => {
  const fallbackHourlyRate = reservationType === "individual" ? 30 : 50;
  const baseHourlyRate = pricingRules?.[reservationType]?.hourlyRate ?? fallbackHourlyRate;
  const iyteEligible = isIyteDiscountEligible(userEmail);
  const priceMultiplier = iyteEligible ? 1 : 3;
  const hourlyRate = baseHourlyRate * priceMultiplier;

  return {
    baseHourlyRate,
    hourlyRate,
    iyteEligible,
    priceMultiplier,
    totalPrice: hourlyRate * duration,
  };
};

/**
 * Toplam fiyat hesapla
 * @param {string} reservationType - "individual" | "group"
 * @param {number} duration - Süre (saat)
 */
export const calculatePrice = (reservationType, duration, userEmail = "", pricingRules = {}) => (
  getPriceBreakdown(reservationType, duration, userEmail, pricingRules).totalPrice
);

/**
 * Süre hesapla (saat cinsinden)
 * @param {string} startTime - "HH:mm"
 * @param {string} endTime - "HH:mm"
/**
 * Tarih formatla (YYYY-MM-DD)
 */
export const formatDate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ========== Dekont Yönetimi ==========

/**
 * Dekont yükle
 * @param {string} reservationId - Rezervasyon ID
 * @param {string} receiptUrl - Yüklenen dekont URL (Storage)
 */
export const uploadReceipt = async (reservationId, receiptUrl) => {
  try {
    const result = await uploadReceiptCallable({ reservationId, receiptUrl });
    return result.data;
  } catch (error) {
    console.error("Error uploading receipt:", error);
    throw error;
  }
};

/**
 * Dekont onayla (Admin)
 * @param {string} reservationId - Rezervasyon ID
 */
export const approveReceipt = async (reservationId) => {
  try {
    const result = await approveReceiptCallable({ reservationId });
    return result.data;
  } catch (error) {
    console.error("Error approving receipt:", error);
    throw error;
  }
};

/**
 * Dekont reddet (Admin)
 * @param {string} reservationId - Rezervasyon ID
 * @param {string} reason - Reddetme sebebi
 * @param {boolean} isFake - Sahte dekont mu?
 */
export const rejectReceipt = async (reservationId, reason = "", isFake = false) => {
  try {
    const result = await rejectReceiptCallable({ reservationId, reason, isFake });
    return result.data;
  } catch (error) {
    console.error("Error rejecting receipt:", error);
    throw error;
  }
};

/**
 * Kullanıcıya ceza ver (Admin)
 * @param {string} userId - Kullanıcı ID
 * @param {string} reason - Ceza sebebi
 */
export const addPenalty = async (userId, reason) => {
  try {
    const result = await addPenaltyCallable({ userId, reason });
    return result.data;
  } catch (error) {
    console.error("Error adding penalty:", error);
    throw error;
  }
};

/**
 * Bekleyen dekontları getir (Admin)
 */
export const getPendingReceipts = async () => {
  try {
    const result = await getPendingReceiptsCallable();
    return result.data.receipts;
  } catch (error) {
    console.error("Error fetching pending receipts:", error);
    throw error;
  }
};

// ========== Tekrarlayan Dersler ==========

/**
 * Tekrarlayan ders oluştur (Admin)
 * @param {Object} eventData - Ders bilgileri
 */
export const createRecurringEvent = async (eventData) => {
  try {
    const result = await createRecurringEventCallable(eventData);
    return result.data;
  } catch (error) {
    console.error("Error creating recurring event:", error);
    throw error;
  }
};

/**
 * Tekrarlayan ders güncelle (Admin)
 * @param {string} eventId - Ders ID
 * @param {Object} updates - Güncellenecek alanlar
 */
export const updateRecurringEvent = async (eventId, updates) => {
  try {
    const result = await updateRecurringEventCallable({ eventId, updates });
    return result.data;
  } catch (error) {
    console.error("Error updating recurring event:", error);
    throw error;
  }
};

/**
 * Tekrarlayan ders sil (Admin)
 * @param {string} eventId - Ders ID
 */
export const deleteRecurringEvent = async (eventId) => {
  try {
    const result = await deleteRecurringEventCallable({ eventId });
    return result.data;
  } catch (error) {
    console.error("Error deleting recurring event:", error);
    throw error;
  }
};

/**
 * Tekrarlayan dersleri getir
 * @param {Object} filters - Filtreleme parametreleri
 */
export const getRecurringEvents = async (filters = {}) => {
  try {
    const result = await getRecurringEventsCallable(filters);
    return result.data.events;
  } catch (error) {
    console.error("Error fetching recurring events:", error);
    throw error;
  }
};


